import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Dialog, Text } from "@nbs/ui";
import { useTranslation } from "react-i18next";

export type AppError = {
  id: string;
  message: string;
  title?: string;
};

type ErrorContextValue = {
  error: AppError | null;
  report: (message: string, title?: string) => void;
  clear: () => void;
};

const ErrorContext = createContext<ErrorContextValue | null>(null);

export function ErrorProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [error, setError] = useState<AppError | null>(null);

  const clear = useCallback(() => setError(null), []);

  const report = useCallback(
    (message: string, title?: string) => {
      setError({
        id: `err-${Date.now()}`,
        message,
        ...(title !== undefined ? { title } : { title: t("pages.errorTitle") }),
      });
    },
    [t],
  );

  useEffect(() => {
    const onUnhandled = (event: ErrorEvent) => {
      report(event.message || t("errors.generic"));
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : t("errors.generic");
      report(message);
    };
    window.addEventListener("error", onUnhandled);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onUnhandled);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, [report, t]);

  const value = useMemo(() => ({ error, report, clear }), [error, report, clear]);

  return (
    <ErrorContext.Provider value={value}>
      {children}
      <Dialog
        open={Boolean(error)}
        title={error?.title ?? t("pages.errorTitle")}
        onClose={clear}
        cancelLabel={t("pages.goHome")}
      >
        <Text>{error?.message}</Text>
      </Dialog>
    </ErrorContext.Provider>
  );
}

export function useErrorContext(): ErrorContextValue {
  const ctx = useContext(ErrorContext);
  if (!ctx) {
    throw new Error("useErrorContext must be used within ErrorProvider");
  }
  return ctx;
}
